<template>
    <div class="facilitator-page">
        <header class="fac-header">
            <div class="fac-header-inner">
                <h1 class="fac-title">{{ $t('facilitator.title') }}</h1>
                <div class="fac-sub">
                    <span class="fac-seminar">{{ $t('facilitator.seminarLabel') }}: {{ seminarId || '—' }}</span>
                    <div class="fac-actions">
                        <button class="fac-export" :disabled="isLoading || !totalRecords" @click="exportCourse">{{ $t('facilitator.export') }}</button>
                        <button class="fac-refresh" :disabled="isLoading" @click="loadData">{{ $t('facilitator.refresh') }}</button>
                    </div>
                </div>
            </div>
        </header>

        <main class="fac-body">
            <div class="fac-toolbar">
                <div class="fac-tabs" role="tablist" :aria-label="$t('facilitator.title')">
                    <button v-for="tab in tabs" :key="tab.id" role="tab" :aria-selected="activeTab === tab.id" :class="{ active: activeTab === tab.id }" @click="activeTab = tab.id">
                        {{ $t(tab.label) }} <span>{{ tab.count }}</span>
                    </button>
                </div>
                <div class="fac-filters">
                    <label>{{ $t('facilitator.team') }}
                        <select v-model="selectedTeam"><option value="">{{ $t('facilitator.allTeams') }}</option><option v-for="id in teamIds" :key="id" :value="id">{{ teamLabel(id) }}</option></select>
                    </label>
                    <label>{{ $t('facilitator.period') }}
                        <select v-model="selectedPeriod"><option value="">{{ $t('facilitator.allPeriods') }}</option><option v-for="period in periods" :key="period" :value="String(period)">{{ $t('facilitator.period') }} {{ period }}</option></select>
                    </label>
                </div>
            </div>
            <p v-if="!isLoading && incomplete" class="fac-warning" role="status">{{ $t('facilitator.partialWarning') }}</p>
            <div v-if="!seminarId" class="fac-state">{{ $t('facilitator.noSeminar') }}</div>
            <div v-else-if="isLoading" class="fac-state">{{ $t('facilitator.loading') }}</div>
            <div v-else-if="errorMsg" class="fac-state fac-error">{{ errorMsg }}</div>
            <div v-else-if="!visibleTeams.length" class="fac-state">{{ $t('facilitator.empty') }}</div>

            <div v-else class="fac-teams">
                <section v-for="team in visibleTeams" :key="team.teamId" class="fac-team">
                    <h2 class="fac-team-name">{{ $t('facilitator.team') }}: {{ teamLabel(team.teamId) }}</h2>

                    <div v-for="pg in team.periods" :key="pg.period" class="fac-period">
                        <div class="fac-period-head">
                            <span class="fac-period-tag">{{ $t('facilitator.period') }} {{ pg.period }}</span>
                            <span class="fac-period-count">{{ $t('facilitator.questionCount', { n: pg.items.length }) }}</span>
                        </div>

                        <template v-for="(item, idx) in pg.items" :key="item.id || item.questionId || idx">
                        <details v-if="activeTab === 'chat'" class="fac-card fac-chat" :open="idx === 0">
                            <summary>
                                <div class="fac-chat-meta">
                                    <span class="fac-label fac-label-q">{{ $t(item.isTrigger ? 'facilitator.automaticTrigger' : 'facilitator.studentQuestion') }}</span>
                                    <span v-if="item.userMobile">{{ maskMobile(item.userMobile) }}</span>
                                    <time v-if="item.createdAt">{{ formatTime(item.createdAt) }}</time>
                                </div>
                                <p class="fac-text">{{ item.question || $t('facilitator.emptyQuestion') }}</p>
                            </summary>
                            <div class="fac-a">
                                <span class="fac-label fac-label-a">{{ $t('facilitator.aiAnswer') }}</span>
                                <p class="fac-text" :class="{ 'fac-muted': !item.answer }">{{ item.answer || $t('facilitator.noReply') }}</p>
                                <p v-if="item.error" class="fac-text fac-error">{{ item.error }}</p>
                            </div>
                        </details>
                        <div v-else class="fac-card">
                            <div v-if="item.triggeredBy" class="fac-trigger">
                                {{ $t('facilitator.triggeredBy') }}: {{ item.triggeredBy }}
                            </div>
                            <div class="fac-q">
                                <span class="fac-label fac-label-q">{{ $t('facilitator.question') }}</span>
                                <p class="fac-text">{{ item.question }}</p>
                            </div>
                            <div class="fac-a" :class="{ 'fac-a-empty': !item.answer }">
                                <span class="fac-label fac-label-a">{{ $t('facilitator.answer') }}</span>
                                <template v-if="item.answer">
                                    <p class="fac-text">{{ item.answer }}</p>
                                    <div class="fac-meta">
                                        <span v-if="item.answerMobile">
                                            {{ $t('facilitator.answeredBy') }}: {{ maskMobile(item.answerMobile) }}
                                        </span>
                                        <span v-if="item.answeredAt">{{ formatTime(item.answeredAt) }}</span>
                                    </div>
                                </template>
                                <p v-else class="fac-text fac-muted">{{ $t('facilitator.unanswered') }}</p>
                            </div>
                        </div>
                        </template>
                    </div>
                </section>
            </div>
        </main>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { setLocale } from '../i18n';
import { loadSocraticBySeminar, loadAiHistoryBySeminar } from '../plugins/ai-chat/utils/apiClient';
import { filterTeams, maskMobile, courseCsv } from './facilitatorQa.mjs';

const { t } = useI18n();

// 从 /facilitator/:seminarId 解析 seminarId
const seminarId = ref(parseSeminarId());
function parseSeminarId() {
    const m = window.location.pathname.match(/^\/facilitator\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
}

// 语言：沿用 ?lang= 或 localStorage 里 SSO 参数的 lang
(function initLocale() {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    let lang = urlLang;
    if (!lang) {
        try {
            const stored = JSON.parse(localStorage.getItem('sso_params') || '{}');
            lang = stored.lang;
        } catch { /* noop */ }
    }
    if (lang) setLocale(lang);
})();

const isLoading = ref(false);
const activeTab = ref('chat');
const selectedTeam = ref('');
const selectedPeriod = ref('');
const chatTeams = ref([]);
const socraticTeams = ref([]);
const chatError = ref('');
const socraticError = ref('');
const historyIncomplete = ref(false);
const incomplete = computed(() => historyIncomplete.value || !!chatError.value || !!socraticError.value);
const errorMsg = computed(() => activeTab.value === 'chat' ? chatError.value : socraticError.value);
const count = teams => teams.reduce((total, team) => total + team.periods.reduce((sum, period) => sum + period.items.length, 0), 0);
const totalRecords = computed(() => count(chatTeams.value) + count(socraticTeams.value));
const tabs = computed(() => [{ id: 'chat', label: 'facilitator.chatTab', count: count(chatTeams.value) }, { id: 'socratic', label: 'facilitator.socraticTab', count: count(socraticTeams.value) }]);
const visibleTeams = computed(() => filterTeams(activeTab.value === 'chat' ? chatTeams.value : socraticTeams.value, selectedTeam.value, selectedPeriod.value));
const teamIds = computed(() => [...new Set([...chatTeams.value, ...socraticTeams.value].map(team => String(team.teamId)))].sort());
const periods = computed(() => [...new Set([...chatTeams.value, ...socraticTeams.value].flatMap(team => team.periods.map(group => group.period)))].sort((a, b) => a - b));
const teamNameMap = ref({}); // teamId -> companyName

const loadData = async () => {
    if (!seminarId.value) return;
    isLoading.value = true;
    chatError.value = '';
    socraticError.value = '';
    try {
        const [history, socratic] = await Promise.allSettled([
            loadAiHistoryBySeminar(seminarId.value),
            loadSocraticBySeminar(seminarId.value),
            loadTeamNames()
        ]);
        for (const [response, destination, error] of [[history, chatTeams, chatError], [socratic, socraticTeams, socraticError]]) {
            if (response.status === 'fulfilled' && response.value?.isSuccess && response.value.result) destination.value = response.value.result.teams || [];
            else { destination.value = []; error.value = t('facilitator.loadFailed'); }
        }
        historyIncomplete.value = history.status === 'fulfilled' && history.value?.result?.complete === false;
    } catch (e) {
        console.error('load facilitator data failed:', e);
        chatError.value = t('facilitator.loadFailed');
        socraticError.value = t('facilitator.loadFailed');
    } finally {
        isLoading.value = false;
    }
};

// 通过已有的 seminar/load 接口拿到 companyAssignment，构建 teamId -> companyName 映射
const loadTeamNames = async () => {
    try {
        const res = await fetch('/api-proxy/service/seminar/load', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ seminarId: seminarId.value })
        });
        const data = await res.json();
        const assignment = (data && data.result && data.result.companyAssignment) || [];
        const map = {};
        assignment.forEach((c) => {
            const name = c.companyName || ('#' + c.companyId);
            (c.teamList || []).forEach((tid) => {
                if (tid) map[String(tid)] = name;
            });
        });
        teamNameMap.value = map;
    } catch (e) {
        // 映射失败不阻断主流程，回退为显示 teamId
        console.warn('load team names failed, fallback to teamId:', e && e.message);
    }
};

const teamLabel = (teamId) => teamNameMap.value[String(teamId)] || teamId;

const formatTime = (val) => {
    if (!val) return '';
    // 后端可能返回 ISO 字符串、Date 或 unix 秒/毫秒
    let d;
    if (typeof val === 'number') {
        d = new Date(val < 1e12 ? val * 1000 : val);
    } else {
        d = new Date(val);
    }
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const exportCourse = () => {
    const csv = courseCsv({ seminarId: seminarId.value, chats: chatTeams.value, socratic: socraticTeams.value, teamLabel, t, formatTime });
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `marksimos-ai-${seminarId.value.replace(/[^\w-]/g, '')}${incomplete.value ? '-partial' : ''}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

onMounted(loadData);
</script>

<style scoped>
.facilitator-page {
    height: 100vh;
    overflow-y: auto;
    background: #f5f5f7;
    color: #1d1d1f;
    font-family: -apple-system, 'SF Pro Text', 'Helvetica Neue', sans-serif;
}

.fac-header {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.1);
}

.fac-header-inner {
    max-width: 880px;
    margin: 0 auto;
    padding: 16px 20px;
}

.fac-actions, .fac-filters, .fac-tabs { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.fac-export { border: 1px solid #d2d7e0; background: white; color: #244eb8; border-radius: 8px; padding: 6px 14px; cursor: pointer; }
.fac-export:disabled { opacity: .4; cursor: not-allowed; }
.fac-toolbar { display: flex; flex-direction: column; gap: 18px; margin-bottom: 24px; }
.fac-tabs { border-bottom: 1px solid #dce0e7; }
.fac-tabs button { background: transparent; border: 0; border-bottom: 2px solid transparent; color: #627084; cursor: pointer; padding: 12px 8px; font-size: 14px; }
.fac-tabs button.active { border-bottom-color: #0071e3; color: #0071e3; font-weight: 600; }
.fac-tabs span { border-radius: 12px; background: #e9edf4; padding: 2px 7px; margin-left: 4px; font-size: 12px; }
.fac-filters label { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #627084; }
.fac-filters select { border: 1px solid #d2d7e0; border-radius: 6px; padding: 7px 10px; max-width: 280px; background: white; color: #1d1d1f; }
.fac-warning { padding: 12px 16px; border-radius: 8px; background: #fff4dc; color: #865b09; font-size: 13px; line-height: 1.5; }
.fac-chat summary { cursor: pointer; }
.fac-chat .fac-a { margin-top: 14px; }
.fac-chat-meta { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 12px; font-size: 12px; color: #627084; margin-bottom: 8px; }
.fac-chat-meta .fac-label { margin-bottom: 0; }

.fac-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.3px;
}

.fac-sub {
    margin-top: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.fac-seminar {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.5);
}

.fac-refresh {
    border: none;
    background: #0071e3;
    color: #fff;
    border-radius: 980px;
    padding: 6px 16px;
    font-size: 13px;
    cursor: pointer;
    transition: filter 0.2s;
}

.fac-refresh:hover:not(:disabled) { filter: brightness(1.1); }
.fac-refresh:disabled { opacity: 0.4; cursor: not-allowed; }

.fac-body {
    max-width: 880px;
    margin: 0 auto;
    padding: 20px;
}

.fac-state {
    text-align: center;
    color: rgba(0, 0, 0, 0.4);
    font-size: 14px;
    padding: 60px 0;
}

.fac-error { color: #ff3b30; }

.fac-team {
    margin-bottom: 32px;
}

.fac-team-name {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 12px;
    padding-bottom: 8px;
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.1);
}

.fac-period {
    margin-bottom: 18px;
}

.fac-period-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.fac-period-tag {
    background: #1d1d1f;
    color: #fff;
    border-radius: 6px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 500;
}

.fac-period-count {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.4);
}

.fac-card {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.fac-trigger {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.4);
    margin-bottom: 10px;
}

.fac-q { margin-bottom: 12px; }

.fac-label {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    margin-bottom: 4px;
}

.fac-label-q { color: #0071e3; }
.fac-label-a { color: #34c759; }

.fac-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
}

.fac-a {
    background: #f5f5f7;
    border-radius: 8px;
    padding: 12px;
}

.fac-a-empty { background: transparent; padding: 0; }

.fac-muted { color: rgba(0, 0, 0, 0.36); font-style: italic; }

.fac-meta {
    margin-top: 8px;
    display: flex;
    gap: 14px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.4);
}
</style>
